<script setup lang="ts">
import { computed } from 'vue'
import { logApiError } from '~/utils/logger'

// Get current user for email auto-fill
const { user, isAuthenticated } = useUser()

// Load published feeds from the API
const { getPublishedFeeds } = useFeestFeeds()
const inspirationCards = ref<Array<{ title: string, text: string, image_url: string | null, color: string | null, span: string | null }>>([])

onMounted(async () => {
  try {
    const { data } = await getPublishedFeeds({ limit: 50 })

    inspirationCards.value = data
  }
  catch (error) {
    logApiError('/api/feest-feeds', error, { operation: 'loadPublicFeeds' })
  }
})

const { setSeoMeta, getBreadcrumbSchema } = useSeo()

setSeoMeta({
  title: 'FeestFabrik - Kinderfeestjes',
  description:
    'waar verjaardagsfeestjes en workshops samen gebouwd worden',
  image: '/images/og-feesten.jpg',
  keywords: [
    'kinderfeestje',
    'verjaardagsfeest kinderen',
    'feestje organiseren',
    'themafeest kinderen',
    'kinderfeestjes Zemst',
  ],
})

// Branded OG image with custom background
defineOgImage('SpeelFabrik', {
  title: 'FeestFabrik',
  description: 'waar verjaardagsfeestjes en workshops samen gebouwd worden',
  backgroundImage: '/images/feesten.png',
})

useSchemaOrg([
  getBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Feesten', path: '/feesten' },
  ]),
])

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'FeestFabrik',
    subtitle: 'waar verjaardagsfeestjes en workshops samen gebouwd worden',
    icon: 'bx-party',
    iconColor: 'secondary',
  },
})

// Form state
const parentName = ref('')
const childName = ref('')
const childAge = ref<number | null>(null)
const manualEmail = ref('')
const phone = ref('')
const partyDate = ref('')
const message = ref('')
const isLoading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Use authenticated user's email or manual input
const email = computed(() => isAuthenticated.value ? user.value?.email || '' : manualEmail.value)

// Handle form submission
const handleSubmit = async () => {
  // Reset messages
  successMessage.value = ''
  errorMessage.value = ''

  // Validate required fields
  if (!parentName.value || !childName.value || !childAge.value || !email.value || !phone.value || !partyDate.value) {
    errorMessage.value = 'Vul alle verplichte velden in'

    return
  }

  isLoading.value = true

  try {
    const response = await $fetch('/api/parties/register', {
      method: 'POST',
      body: {
        parent_name: parentName.value,
        child_name: childName.value,
        child_age: childAge.value,
        email: email.value,
        phone: phone.value,
        party_date: partyDate.value,
        message: message.value,
      },
    })

    successMessage.value = response.message

    // Reset form
    parentName.value = ''
    childName.value = ''
    childAge.value = null
    manualEmail.value = ''
    phone.value = ''
    partyDate.value = ''
    message.value = ''
  }
  catch (error) {
    logApiError('/api/parties/register', error)

    const err = error as { data?: { message?: string }, message?: string }

    errorMessage.value
      = err.data?.message || err.message || 'Er is iets misgegaan. Probeer het opnieuw.'
  }
  finally {
    isLoading.value = false
  }
}
</script>

<template>
  <VContainer>
    <VRow>
      <!-- Info Column -->
      <VCol cols="12" md="6">
        <VCard>
          <VCardText class="pa-8">
            <div class="text-center mb-6">
              <VIcon icon="bx-party" size="64" color="secondary" class="mb-4" />
              <h2 class="text-h4">
                Feestjes en workshops voor kinderen
              </h2>
            </div>

            <p class="text-body-1 text-medium-emphasis mb-4">
              Welkom in onze FeestFabrik, waar fantasie en plezier samenkomen. Wij organiseren verjaardagsfeestjes en creatieve workshops, bij jou thuis of op verplaatsing. Samen gaan we op zoek naar de perfecte locatie.
            </p>

            <p class="text-body-1 text-medium-emphasis mb-4">
              Van selfcare-momentjes tot schilderen, grimmen, kleien en het ontwerpen van een eigen bloempot: elk kind mag hier stralen en creëren op zijn of haar eigen tempo.
            </p>

            <p class="text-body-1 text-medium-emphasis mb-4">
              Afhankelijk van de leeftijd en noden zoeken we samen naar een gepast aanbod, voor een ervaring die nog lang blijft nazinderen.
            </p>

            <VAlert
              type="info"
              variant="tonal"
              icon="bx-bulb"
              density="comfortable"
            >
              In de feestfeeds hieronder kan je kiezen welk themafeestje of welke workshop jij graag wil op jouw feestje!
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Contact Form Column -->
      <VCol cols="12" md="6">
        <VCard>
          <VCardItem>
            <VCardTitle class="d-flex align-center gap-2">
              <VIcon icon="bx-envelope" size="24" />
              Vraag info aan
            </VCardTitle>
          </VCardItem>

          <VCardText>
            <VForm @submit.prevent="handleSubmit">
              <VTextField
                v-model="parentName"
                label="Naam ouder *"
                :disabled="isLoading"
                class="mb-4"
              />

              <VRow class="mb-1">
                <VCol cols="12" md="8">
                  <VTextField
                    v-model="childName"
                    label="Naam kind *"
                    :disabled="isLoading"
                  />
                </VCol>

                <VCol cols="12" md="4">
                  <VTextField
                    v-model.number="childAge"
                    label="Leeftijd kind *"
                    type="number"
                    min="3"
                    max="18"
                    :disabled="isLoading"
                  />
                </VCol>
              </VRow>

              <VTextField
                v-if="!isAuthenticated"
                v-model="manualEmail"
                label="E-mailadres *"
                type="email"
                :disabled="isLoading"
                class="mb-4"
              />
              <VTextField
                v-else
                :model-value="user?.email || ''"
                label="E-mailadres"
                type="email"
                readonly
                variant="outlined"
                bg-color="grey-lighten-4"
                hint="Je bent ingelogd, je e-mailadres is reeds gekend"
                persistent-hint
                class="mb-4"
              />

              <VTextField
                v-model="phone"
                label="GSM-nummer *"
                :disabled="isLoading"
                class="mb-4"
              />

              <VTextField
                v-model="partyDate"
                label="Datum feestje *"
                type="date"
                :disabled="isLoading"
                class="mb-4"
              />

              <VTextarea
                v-model="message"
                label="Bericht"
                :disabled="isLoading"
                rows="4"
                class="mb-4"
              />

              <VAlert
                v-if="successMessage"
                type="success"
                closable
                class="mb-4"
                @click:close="successMessage = ''"
              >
                {{ successMessage }}
              </VAlert>

              <VAlert
                v-if="errorMessage"
                type="error"
                closable
                class="mb-4"
                @click:close="errorMessage = ''"
              >
                {{ errorMessage }}
              </VAlert>

              <VBtn
                type="submit"
                color="primary"
                block
                :loading="isLoading"
                :disabled="isLoading"
              >
                <VIcon icon="bx-send" start />
                Verstuur aanvraag
              </VBtn>
            </VForm>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Inspiration Feed Section -->
    <div v-if="inspirationCards.length" class="inspiration-section mt-12 mb-8">
      <div class="text-center mb-8">
        <VIcon icon="bx-bulb" size="48" color="secondary" class="mb-2" />
        <h2 class="text-h4 font-weight-bold mb-2">
          Inspiratie & Ideeën
        </h2>
        <p class="text-body-1 text-medium-emphasis mx-auto" style="max-inline-size: 600px;">
          Ontdek onze workshops en activiteiten. Van creatief tot actief — er is voor elk kind iets leuks!
        </p>
      </div>

      <div class="masonry-grid">
        <div
          v-for="(card, index) in inspirationCards"
          :key="index"
          class="masonry-item"
          :class="[`masonry-item--${card.span}`]"
        >
          <VCard
            class="masonry-card h-100"
            :color="card.color || 'pink-lighten-4'"
            flat
          >
            <VImg
              v-if="card.image_url"
              :src="card.image_url"
              :alt="card.title"
              cover
              class="card-image"
            />
            <VCardText class="pa-5 d-flex flex-column">
              <h3 class="text-h5 font-weight-black mb-3 card-title">
                {{ card.title }}
              </h3>
              <p class="text-body-2 text-medium-emphasis flex-grow-1 card-text">
                {{ card.text }}
              </p>
            </VCardText>
          </VCard>
        </div>
      </div>
    </div>
  </VContainer>
</template>

<style scoped lang="scss">
.inspiration-section {
  .masonry-grid {
    column-gap: 1.25rem;
    columns: 3;

    @media (max-width: 960px) {
      columns: 2;
    }

    @media (max-width: 600px) {
      columns: 1;
    }
  }

  .masonry-card {
    overflow: hidden;
    border-radius: 16px !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    &:hover {
      box-shadow: 0 8px 30px rgb(0 0 0 / 12%) !important;
      transform: translateY(-4px);
    }
  }

  .card-image {
    block-size: 260px;
  }

  .masonry-item {
    break-inside: avoid;
    margin-block-end: 1.25rem;

    &--tall {
      .card-image {
        block-size: 340px;
      }
    }
  }

  .card-title {
    font-family: "Comic Neue", Nunito, sans-serif;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  .card-text {
    line-height: 1.65;
  }
}
</style>
